import { Collection, ProjectLaborDistribution, ProjectLaborDistributionEntry } from '@/models'

import { sampleLaborDistribution } from '../../../data/sampleLaborDistribution.ts'
/**
 * Returns the Date corresponding to the Monday of the current week.
 * - If today is Monday, it returns today's date.
 * - If today is any day from Tuesday to Saturday, it returns the Monday of the current week.
 * - If today is Sunday, it returns the following Monday.
 */
export function getCurrentMonday(): Date {
	const today: Date = new Date()
	const jsDay: number = today.getDay()

	// Calculate offset:
	// - If today is Sunday (jsDay === 0), set the offset to -1 so we move forward one day (to Monday).
	// - Otherwise, offset is jsDay - 1 (So Monday (1) gives 0 offset)
	const offset: number = jsDay === 0 ? -1 : jsDay - 1

	// Create a new Date instance ro avoid mutating 'today'
	const monday: Date = new Date(today)
	monday.setDate(today.getDate() - offset)
	return monday
}

/**
 * Returns an array of Dates representing consecutive Mondays.
 *
 * @param count - The number of Mondays to generate (including the initial Monday).
 */
export function getNextMondays(count: number): Date[] {
	const mondays: Date[] = []
	const initialMonday: Date = getCurrentMonday()

	for (let i = 0; i < count; i++) {
		// Clone initial Monday to avoid mutating it.
		const nextMonday: Date = new Date(initialMonday)
		// Add i weeks (i* 7 days) to the initial Monday.
		nextMonday.setDate(initialMonday.getDate() + i * 7)
		mondays.push(nextMonday)
	}
	return mondays
}

/**
 * Calculate the project duration in weeks based on the start and end dates.
 * Here we use the ceiling of the difference in days divided by 7.
 * We also cap the weeks to the maximum defined in our distribution table (16).
 */
export function getProjectWeeks(startDate: Date, endDate: Date): number {
	const msPerDay = 1000 * 60 * 60 * 24
	const diffDays = (endDate.getTime() - startDate.getTime()) / msPerDay
	let weeks = Math.ceil(diffDays / 7)
	if (weeks < 1) {
		weeks = 1
	}
	if (weeks > 16) {
		weeks = 16
	}
	return weeks
}

/**
 * Given a date, find the Friday in that week.
 *
 * For our purposes the "zero" week is defined by the Friday of the week that includes the end date.
 * If the end date falls before Friday (i.e. Monday–Thursday), we move forward to that week’s Friday.
 * If the end date is after Friday (i.e. Saturday-Sunday), we move backward.
 */
export function getEndFriday(endDate: Date): Date {
	const endFriday = new Date(endDate)
	const day = endFriday.getDay() // Sunday=0, Monday=1, ... Saturday=6
	let offset: number
	if (day === 5) {
		offset = 0 // Already Friday
	} else if (day === 0) {
		offset = -2 // move back to Friday (from Sunday)
	} else if (day < 5) {
		offset = 5 - day // move forward to Friday (from Monday-Thursday)
	} else {
		offset = -1 // move back to Friday (from Saturday)
	}
	endFriday.setDate(endFriday.getDate() + offset)
	return endFriday
}

/**
 * Distributes a total number of labor hours over a series of weeks.
 *
 * The distribution is based on a table whose columns are week offsets relative to
 * an anchor Friday (the Friday of the week of the project end date).
 * For each column (i.e. each Friday), we compute the Monday of that same week
 * (by subtracting 4 days) and return that date with the allocated hours.
 *
 * @param totalHours - The total labor hours (e.g. 2000)
 * @param startDate  - The project start date
 * @param endDate    - The project end date (used to determine the anchor Friday)
 *
 * @returns An array of objects where each object contains a Monday date and its allocated hours.
 */
export function distributeBuildLaborHours(
	id: number,
	shop: string,
	totalHours: number,
	startDate: Date,
	endDate: Date
): ProjectLaborDistribution {
	// 1. Determine the project duration in weeks.
	const projectWeeks = getProjectWeeks(startDate, endDate)

	// 2. Look up the corresponding distribution row (the keys are strings).
	const distributionRow: number[] = sampleLaborDistribution.durationInWeeks[projectWeeks.toString()]
	if (!distributionRow) {
		throw new Error(`No distribution data for a project length of ${projectWeeks} weeks.`)
	}

	// 3. Compute the "anchor" Friday (the Friday for the zero week) from the end date.
	const anchorFriday = getEndFriday(endDate)

	// 4. For each column (week offset), calculate the Friday date, then the Monday of that week.

	const entries = new Collection(ProjectLaborDistributionEntry, [])
	const { weeksOffsetFromShip } = sampleLaborDistribution
	for (let i = 0; i < weeksOffsetFromShip.length; i++) {
		const weekOffset = weeksOffsetFromShip[i]
		// Clone the anchor Friday and add (weekOffset * 7) days to get the actual Friday.
		const fridayDate = new Date(anchorFriday)
		fridayDate.setDate(anchorFriday.getDate() + weekOffset * 7)

		// Determine the Monday of the same week.
		// Since Friday is always 4 days after Monday, subtract 4 days.
		const mondayDate = new Date(fridayDate)
		mondayDate.setDate(fridayDate.getDate() - 4)

		// Multiply the percentage (divided by 100) by the total hours.
		const percentage = distributionRow[i]
		const hours = (percentage / 100) * totalHours
		if (hours > 0) {
			entries.add(new ProjectLaborDistributionEntry({ date: mondayDate, hours }))
			// entries.push({ date: mondayDate, hours })
		}
	}
	return new ProjectLaborDistribution({ id, shop, entries })
}

/**
 * Distributes labor hours evenly (linearly) across the project timeline.
 *
 * This allocates hours proportionally to each week based on actual working days,
 * accounting for partial weeks at the start and end of the project.
 *
 * @param id - The project/forecast ID
 * @param shop - The shop/location code
 * @param totalHours - The total labor hours to distribute (should be weighted hours)
 * @param startDate - The project start date (production kickoff)
 * @param endDate - The project end date (ship date)
 * @returns A ProjectLaborDistribution with evenly distributed hours
 */
export function distributeLinearLaborHours(
	id: number,
	shop: string,
	totalHours: number,
	startDate: Date,
	endDate: Date
): ProjectLaborDistribution {
	// 1. Find the Monday of the start week
	const startMonday = new Date(startDate)
	const startDay = startMonday.getDay()
	// Adjust to the Monday of the start week
	if (startDay === 0) {
		// If Sunday, go back 6 days
		startMonday.setDate(startMonday.getDate() - 6)
	} else if (startDay !== 1) {
		// If not Monday, go back to Monday
		startMonday.setDate(startMonday.getDate() - (startDay - 1))
	}

	// 2. Find the Monday of the end week
	const endMonday = new Date(endDate)
	const endDay = endMonday.getDay()
	// Adjust to the Monday of the end week
	if (endDay === 0) {
		// If Sunday, go back 6 days
		endMonday.setDate(endMonday.getDate() - 6)
	} else if (endDay !== 1) {
		// If not Monday, go back to Monday
		endMonday.setDate(endMonday.getDate() - (endDay - 1))
	}

	// 3. Calculate the working days for each week
	const weekData: { monday: Date; workDays: number }[] = []
	const currentMonday = new Date(startMonday)

	while (currentMonday <= endMonday) {
		const weekStart = new Date(currentMonday)
		const weekEnd = new Date(currentMonday)
		weekEnd.setDate(weekEnd.getDate() + 4) // Friday of the same week

		let workDays = 5 // Default to full week (Mon-Fri)

		// Check if this is the first week
		if (weekStart.getTime() === startMonday.getTime()) {
			// Calculate working days from start date to Friday
			const startDayOfWeek = startDate.getDay()
			if (startDayOfWeek >= 1 && startDayOfWeek <= 5) {
				// Monday = 1, Friday = 5
				workDays = 6 - startDayOfWeek // Days from start to Friday inclusive
			} else if (startDayOfWeek === 0) {
				// Sunday - count full week
				workDays = 5
			} else {
				// Saturday - count full week for next week
				workDays = 5
			}
		}

		// Check if this is the last week
		if (weekStart.getTime() === endMonday.getTime()) {
			// Calculate working days from Monday to end date
			const endDayOfWeek = endDate.getDay()
			if (endDayOfWeek >= 1 && endDayOfWeek <= 5) {
				// If we're in the last week, take the minimum of calculated days
				const daysToEnd = endDayOfWeek // Monday = 1, so this gives us days from Monday
				workDays = weekStart.getTime() === startMonday.getTime() ? Math.min(workDays, daysToEnd) : daysToEnd
			} else if (endDayOfWeek === 0) {
				// Sunday - count up to Friday
				workDays = weekStart.getTime() === startMonday.getTime() ? Math.min(workDays, 5) : 5
			} else {
				// Saturday - count up to Friday
				workDays = weekStart.getTime() === startMonday.getTime() ? Math.min(workDays, 5) : 5
			}
		}

		weekData.push({ monday: new Date(currentMonday), workDays })
		currentMonday.setDate(currentMonday.getDate() + 7)
	}

	// 4. Calculate total work days
	const totalWorkDays = weekData.reduce((sum, week) => sum + week.workDays, 0)

	// 5. Calculate hours per day and distribute proportionally
	const hoursPerDay = totalWorkDays > 0 ? totalHours / totalWorkDays : 0

	// 6. Create entries for each week with proportional hours
	const entries = new Collection(ProjectLaborDistributionEntry, [])

	for (const week of weekData) {
		const weekHours = hoursPerDay * week.workDays
		entries.add(
			new ProjectLaborDistributionEntry({
				date: week.monday,
				hours: weekHours
			})
		)
	}

	return new ProjectLaborDistribution({ id, shop, entries })
}

/**
 * Returns an array of Dates representing the first day of consecutive months.
 *
 * @param count - The number of months to generate (including the current month).
 */
export function getNextMonths(count: number): Date[] {
	const months: Date[] = []
	const currentDate = new Date()
	const firstOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)

	for (let i = 0; i < count; i++) {
		const nextMonth = new Date(firstOfMonth)
		nextMonth.setMonth(firstOfMonth.getMonth() + i)
		months.push(nextMonth)
	}
	return months
}
